import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "app/store";
import { RestrictedFeatureSource } from "app/types";
import { SpinModal } from "common/elements";
import { TutorialArrow } from "common/assets";
import { btnDelay, useScale, usePlaySound, shouldApplyGuestRestrictions } from "common/utils";
import { AccountType } from "features/user/types";
import { copyProject } from "features/creator/api";
import { actions, selectAllProjects } from "features/creator/slice";
import { Overlay } from "features/creator/tutorial/top/stages/Layout";
import { ProjectType, ProjectPanelType } from "features/creator/types";
import { CreateProjectModal, ProjectLimitDialog } from "features/creator/elements";
import {
  usePlan,
  usePlanLimits,
  PlanType,
  PLAN_LIMITS,
  useEnforcePlanLimit,
} from "features/subscription";

const Step = (props: { back: () => void; next: () => void; close: () => void }) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const { next, close } = props;
  const enforcePlanLimit = useEnforcePlanLimit();
  const plan = usePlan();
  const limits = usePlanLimits();
  const [copy, setCopy] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [guidance, setGuidance] = useState(false);
  const projects = useSelector(selectAllProjects);
  const user = useSelector((state: RootState) => state.user.active);
  const userProjects = useMemo(
    () =>
      projects.filter(
        (project) =>
          project.author_uid === user.uid &&
          !project.deleted_at &&
          project.type === ProjectType.BASIC,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects],
  );
  const officialProjects = useMemo(
    () =>
      projects
        .filter((project) => project.published)
        .filter(
          (project) =>
            project.name === "チュートリアル" &&
            project.author_name === "まなパズ公式" &&
            project.type === ProjectType.BASIC,
        ),
    [projects],
  );
  const [panelRect, setPanelRect] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  useEffect(() => {
    setTimeout(() => {
      const panel = document.getElementById("creator-display-copy-btn");
      const rect = panel.getBoundingClientRect();
      setPanelRect({
        x: rect.x - 10 * scale,
        y: rect.y - 30 * scale,
        width: rect.width + 20 * scale,
        height: rect.width + 20 * scale,
      });
    }, 500);
  }, [scale]);

  const handleClick = () => {
    play();
    btnDelay(() => {
      // OLD TRIALユーザー判定（2025-11-02以前に作成されたGUESTユーザー）
      const isOldTrialUser =
        plan === PlanType.TRIAL &&
        user.account_type === AccountType.GUEST &&
        !shouldApplyGuestRestrictions(user.created_at);

      // 制限値を計算（OLD TRIALはFREEと同じ制限）
      const projectLimit = isOldTrialUser
        ? PLAN_LIMITS[PlanType.FREE].projects.limit
        : limits.projects.limit;

      // 無制限プランはチェックスキップ
      if (limits.projects.unlimited) {
        setCopy(true);
        return;
      }

      // 制限に達している場合
      if (userProjects.length >= projectLimit) {
        // TRIAL は新 Dialog、FREE/STANDARD は RestrictedFeatureDialog
        if (plan === PlanType.TRIAL || plan === PlanType.FREE) {
          enforcePlanLimit(RestrictedFeatureSource.PROJECT_LIMIT);
          close();
          return;
        }
        // EDUCATION/PREMIUM/BUILDER: ProjectLimitDialog表示（通知のみ）
        setGuidance(true);
        return;
      }

      setCopy(true);
    });
  };
  const handleCopyProject = (name: string) => {
    setLoading(true);
    btnDelay(() => {
      dispatch(
        copyProject({
          uid: user.uid,
          copied_project_id: officialProjects[0].project_id,
          name,
        }),
      )
        .then(unwrapResult)
        .then(() => {
          dispatch(
            actions.updateProjectFavorited({
              project_id: officialProjects[0].project_id,
              favorited: officialProjects[0].favorited + 1,
              is_favorited: true,
            }),
          );
          dispatch(actions.updateProjectPanel(ProjectPanelType.USER));
          dispatch(actions.updateSortFilterPanel(false));
          dispatch(
            actions.handleUpdatePublishedProjectFilter({
              type: "onlyOfficial",
              value: false,
            }),
          );
          next();
        });
    });
  };
  const handleCopyProjectCancel = () => {
    play();
    btnDelay(() => setCopy(false));
  };

  // プラン別ダイアログのアクションハンドラ（EDUCATION/STANDARD用）
  // TRIAL/FREEは直接リダイレクトするのでこの関数は呼ばれない
  const _handleGuidanceAction = () => {
    play();
    setGuidance(false);
    close();
  };

  const handleCloseGuidance = () => {
    play();
    setGuidance(false);
    close();
  };

  return (
    <>
      {loading && <SpinModal />}

      <ProjectLimitDialog open={guidance} type="app" close={handleCloseGuidance} />

      <CreateProjectModal
        zIndex={20000}
        visible={copy}
        name="チュートリアル"
        type={ProjectType.BASIC}
        create={handleCopyProject}
        close={handleCopyProjectCancel}
      />

      <Overlay area={panelRect} onClick={handleClick}>
        <div
          className="absolute z-50"
          style={{
            width: 108 * scale,
            height: 105 * scale,
            transform: `translate(${panelRect.x - 90 * scale}px, ${
              panelRect.y + 20 * scale
            }px) rotate(-45deg)`,
          }}
        >
          <TutorialArrow />
        </div>
      </Overlay>
    </>
  );
};

export default Step;
